import React from "react";
import { FormValues } from "components/medications/create-medication/duck";
import {
  UpdateCardProps,
  useInitializeOfflineData,
  useParams,
  convertDoseToMg
} from "duck";
import {
  MedicationCalculationTypeEnum,
  GetAnestheticProtocolDocument,
  RouteEnum,
  useCreateAnestheticProtocolMedicationCalculationMutation,
  useDeleteAnestheticProtocolMedicationCalculationsMutation,
  useUpdateAnestheticProtocolMedicationCalculationMutation
} from "requests";

export const useDeleteMedications = () => {
  const { id: anestheticProtocolId } = useParams();
  const [
    deleteMedications
  ] = useDeleteAnestheticProtocolMedicationCalculationsMutation({
    ignoreResults: true
  });
  const { loadProtocols } = useInitializeOfflineData();

  return (ids: number[]) =>
    deleteMedications({
      variables: { input: { ids } },
      refetchQueries: [
        {
          query: GetAnestheticProtocolDocument,
          variables: { _id: +anestheticProtocolId }
        }
      ]
    }).then(loadProtocols);
};

export const useCreateAnestheticProtocolMedicationCalculation = () => {
  const { id: anestheticProtocolId } = useParams();
  const [create] = useCreateAnestheticProtocolMedicationCalculationMutation({
    ignoreResults: true
  });
  const { loadProtocols, loadMedications } = useInitializeOfflineData();

  return (values: FormValues, medicationType: MedicationCalculationTypeEnum) =>
    create({
      variables: {
        input: {
          anestheticProtocol: +anestheticProtocolId,
          type: medicationType,
          concentration: values.concentration,
          dose: convertDoseToMg(values.dose, values.doseUnit),
          route: values.route as RouteEnum,
          medication: values.name,
          additionalVolumeAdministered: values.additionalVolumeAdministered,
          additionalVolumeDrawn: values.additionalVolumeDrawn,
          volumeAdministered: values.volumeAdministered
        }
      },
      refetchQueries: [
        {
          query: GetAnestheticProtocolDocument,
          variables: { _id: +anestheticProtocolId }
        }
      ]
    }).then(() => {
      loadProtocols();
      loadMedications();
    });
};

export const useUpdateAnestheticProtocolMedicationCalculation = () => {
  const { loadProtocols } = useInitializeOfflineData();
  const [update] = useUpdateAnestheticProtocolMedicationCalculationMutation({
    ignoreResults: true
  });

  return React.useCallback(
    (props: UpdateCardProps) => {
      const { idOrFakeId, ...data } = props;

      if (typeof idOrFakeId === "number") {
        return update({
          variables: { input: { _id: idOrFakeId, ...data } }
        }).then(loadProtocols);
      }
    },
    [update]
  );
};
